import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  PhotoLibrary as PhotoLibraryIcon,
  CloudUpload as CloudUploadIcon,
  BrokenImage as BrokenImageIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchMediaAssetsByPublisher } from '../../features/media/slices/mediaSlice';
import MediaForm from '../../features/media/components/MediaForm';
import { MediaAsset } from '../../types/media.types';

interface ImageUrlSelectorProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  publisherId?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  backgroundGradient?: string;
}

const ImageUrlSelector: React.FC<ImageUrlSelectorProps> = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  publisherId,
  placeholder = "https://...",
  helperText,
  error = false,
  required = false,
  backgroundGradient,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { filteredItems: mediaAssets, isLoading: mediaLoading } = useSelector(
    (state: RootState) => state.media
  );

  const [openLibrary, setOpenLibrary] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);

  // Filter to only show image media assets
  const imageAssets = mediaAssets.filter((asset) =>
    asset.mimeType?.toLowerCase().startsWith('image/')
  );

  // Reset image error state when value changes
  useEffect(() => {
    setImageLoadError(false);
    if (value) {
      setImageLoading(true);
    }
  }, [value]);

  const handleOpenLibrary = () => {
    if (publisherId) {
      // Fetch media assets for the publisher
      dispatch(fetchMediaAssetsByPublisher(publisherId));
      setOpenLibrary(true);
    }
  };

  const handleCloseLibrary = () => {
    setOpenLibrary(false);
    setSelectedMediaId(null);
  };

  const handleSelectImage = (asset: MediaAsset) => {
    setSelectedMediaId(asset.id);
  };

  const handleConfirmSelection = () => {
    const selectedAsset = imageAssets.find((a) => a.id === selectedMediaId);
    if (selectedAsset) {
      const url = selectedAsset.publicUrl || selectedAsset.s3Url || selectedAsset.fileUrl || '';
      onChange({
        target: { name, value: url },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    handleCloseLibrary();
  };

  const handleOpenUpload = () => {
    setOpenUpload(true);
  };

  const handleCloseUpload = () => {
    setOpenUpload(false);
  };

  const handleUploadSuccess = (asset: MediaAsset) => {
    const url = asset.publicUrl || asset.s3Url || asset.fileUrl || '';
    onChange({
      target: { name, value: url },
    } as React.ChangeEvent<HTMLInputElement>);

    // Refresh media library to include the new asset
    if (publisherId) {
      dispatch(fetchMediaAssetsByPublisher(publisherId));
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageLoadError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageLoadError(true);
  };

  const renderPreview = () => {
    if (!value) {
      return (
        <Box
          sx={{
            width: '100%',
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No image URL specified
          </Typography>
        </Box>
      );
    }

    if (imageLoadError) {
      return (
        <Box
          sx={{
            width: '100%',
            height: 120,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'error.light',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'error.main',
            color: 'error.dark',
          }}
        >
          <BrokenImageIcon sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="body2">
            Failed to load image
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: 120,
        background: backgroundGradient,
        display: backgroundGradient ? 'flex' : 'block',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        overflow: 'hidden',
      }}>
        {imageLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              borderRadius: 1,
              zIndex: 1,
            }}
          >
            <CircularProgress size={30} />
          </Box>
        )}
        <Box
          component="img"
          src={value}
          alt="Preview"
          onLoad={handleImageLoad}
          onError={handleImageError}
          sx={{
            width: '100%',
            height: backgroundGradient ? '80%' : '100%',
            objectFit: 'contain',
            borderRadius: 1,
            border: backgroundGradient ? 'none' : '1px solid',
            borderColor: 'divider',
            bgcolor: backgroundGradient ? 'transparent' : 'background.paper',
          }}
        />
      </Box>
    );
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            helperText={helperText}
            error={error}
            required={required}
          />
        </Grid>
        <Grid item xs={12}>
          {renderPreview()}
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<PhotoLibraryIcon />}
              onClick={handleOpenLibrary}
              disabled={disabled || !publisherId}
              size="small"
            >
              Select from Library
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={handleOpenUpload}
              disabled={disabled || !publisherId}
              size="small"
            >
              Upload New Image
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Media Library Dialog */}
      <Dialog
        open={openLibrary}
        onClose={handleCloseLibrary}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Select Image from Library
          <IconButton
            onClick={handleCloseLibrary}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {mediaLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : imageAssets.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No images found in the media library. Upload a new image to get started.
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {imageAssets.map((asset) => (
                <Grid item xs={12} sm={6} md={4} key={asset.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor:
                        selectedMediaId === asset.id ? 'primary.main' : 'transparent',
                      position: 'relative',
                      '&:hover': {
                        borderColor: 'primary.light',
                      },
                    }}
                    onClick={() => handleSelectImage(asset)}
                  >
                    {selectedMediaId === asset.id && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                    )}
                    <CardMedia
                      component="img"
                      height="140"
                      image={asset.publicUrl || asset.s3Url || asset.fileUrl}
                      alt={asset.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ p: 1.5 }}>
                      <Tooltip title={asset.name}>
                        <Typography variant="body2" noWrap>
                          {asset.name}
                        </Typography>
                      </Tooltip>
                      {asset.description && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {asset.description}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLibrary}>Cancel</Button>
          <Button
            onClick={handleConfirmSelection}
            variant="contained"
            disabled={!selectedMediaId}
          >
            Select Image
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload New Image Dialog */}
      <MediaForm
        open={openUpload}
        onClose={handleCloseUpload}
        onSuccess={handleUploadSuccess}
        isEdit={false}
        publisherId={publisherId?.replace('PUBLISHER#', '')}
      />
    </Box>
  );
};

export default ImageUrlSelector;
